import { useEffect, useMemo, useState } from 'react';
import { classificationSourceUnavailable } from '../features/shared/evidence-results';
import { ReturnCentre, configuredObligationsForSubject, filingDeadlineNoticesForSubject, returnContextForObligation, toReturnCentreObligation,
  type TaxObligationId } from '../features/return-centre';
import { PracticeInvoiceWorkflow, PracticeVatWorkflow, TrackAPracticeJourney, bindReviewedVatFacts,
  createPracticeInvoiceDraft, derivePracticeWorkItems, deriveReviewedVatFacts, trackAFixture,
  type PracticeClientSummary, type PracticeVatFactsInput, type TrackAPracticeModel, type TrackAView } from '../features/track-a';
import { bindBusinessCitFacts, BusinessCitReturnInteraction, formatKobo, type BusinessCitFactsInput,
  type InvoiceResourceView, type MockInvoiceReceiptView } from '../features/track-b1';
import { PersonalTaxFactsForm, trackB2Fixture, type PersonalTaxFactsInput } from '../features/track-b2';
import { bindPersonalTaxFacts } from '../features/return-centre';
import { applyEvidenceDuplicatePresentation, applyEvidenceProposalBlockers, applyEvidenceReviewProgress, applyExtractedOwnership, detectEvidenceOwner, evaluateExtractedOwnership, reconciliationFromResult,
  mergeRecoveredEvidence, restorePersistedEvidence, settleEvidence, settleEvidenceWorkflowError,
  purgeEvidenceUsage, type BridgeState, type CalculationSnapshot, type EvidenceItem,
  type IntelligenceState, type ReconciliationItem, type ResponsibilityAction, type ResponsibilityStep,
  type SharedJourneyPorts, type SubmissionEvent } from '../features/shared';
import { fileToBase64, type AccountSubject, type CitPositionResult, type EInvoiceResource, type ReviewedInvoiceFactsInput,
  type TaxCalculationResult, type TaxReturnResource } from '../lib/api';
import type { PersonaWorkspaceProps } from '../types/persona';
import { useProductSurfaceRoute } from '../lib/product-routes';
import { useIOSPresentation } from '../lib/web-presentation';
import { presentAppRecordCopy } from '../features/shared/record-copy';
import { TaxSubjectSetupDialog } from '../components/FirstUseSetup';

interface AssignedClientSubject extends AccountSubject { engagement_id?: string; }

interface ClientWorkspaceState {
  evidence: EvidenceItem[];
  reconciliation: ReconciliationItem[];
  calculation: CalculationSnapshot;
  responsibilities: ResponsibilityStep[];
  submission: SubmissionEvent[];
  bridge: BridgeState;
  invoices: TrackAPracticeModel['invoices'];
  handoffs: TrackAPracticeModel['handoffs'];
  intelligence: IntelligenceState;
  taxReturn?: TaxReturnResource;
  vatCalculation?: TaxCalculationResult;
  vatSnapshotSha256?: string;
  pitReturn?: TaxReturnResource;
  pitCalculation?: TaxCalculationResult;
  pitCalculationView?: CalculationSnapshot;
  pitResponsibilities?: ResponsibilityStep[];
  citCalculation?: CitPositionResult;
  invoice?: EInvoiceResource;
  invoiceReceipt?: MockInvoiceReceiptView;
}

export function PractitionerWorkspace({ api, subjects, onTaxSubjectCreated }: PersonaWorkspaceProps) {
  const [model, setModel] = useState<TrackAPracticeModel>(() => buildInitialModel(subjects));
  const [clientWorkspaces, setClientWorkspaces] = useState<Record<string, ClientWorkspaceState>>(() =>
    buildClientWorkspaces(buildInitialModel(subjects)));
  const ios = useIOSPresentation();
  const practiceViews: readonly TrackAView[] = ios ? ['portfolio', 'evidence', 'returns', 'invoices', 'handoffs', 'intelligence']
    : ['home', 'portfolio', 'evidence', 'returns', 'invoices', 'handoffs', 'intelligence'];
  const [activeView, setActiveView] = useProductSurfaceRoute('practitioner', practiceViews, ios ? 'portfolio' : 'home');
  const [notice, setNotice] = useState('');
  const [selectedReturnObligation, setSelectedReturnObligation] = useState<TaxObligationId>('VAT');
  const [vatBusy, setVatBusy] = useState(false);
  const [pitBusy, setPitBusy] = useState(false);
  const [citBusy, setCitBusy] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const selectedClient = model.clients.find((candidate) => candidate.id === model.selectedClientId);
  const selectedSubject = selectedClient ? subjects.find((subject) => subject.id === selectedClient.id) : undefined;
  const selectedWorkspace = selectedClient ? clientWorkspaces[selectedClient.id] : undefined;
  const vatDerivation = useMemo(() => deriveReviewedVatFacts(selectedWorkspace?.reconciliation ?? [],
    model.contexts.find((context) => context.id === model.currentContextId)?.periodLabel.match(/\d{4}-\d{2}/)?.[0] ?? '2026-07'),
  [model.currentContextId, model.contexts, selectedWorkspace?.reconciliation]);
  const evidenceContext = returnContextForObligation(selectedSubject, selectedReturnObligation)
    ?? selectedSubject?.resolved_contexts?.find((context) => context.scope.rail === 'tax_return');
  const currentWorkItems = useMemo(() => derivePracticeWorkItems(model.clients, clientWorkspaces),
    [clientWorkspaces, model.clients]);

  useEffect(() => {
    if (!selectedClient || !evidenceContext || typeof api.listClassificationJobs !== 'function') return;
    const controller = new AbortController();
    const client = selectedClient;
    api.selectContext(evidenceContext);
    void restorePersistedEvidence(api, evidenceContext, { id: client.id, name: client.name,
      addedBy: 'Current practitioner' }, (recovered) => updateClientWorkspace(client.id,
      (workspace) => mergeRecoveredEvidence(workspace, recovered)), controller.signal).catch((error: unknown) => {
      if (controller.signal.aborted || error instanceof DOMException && error.name === 'AbortError') return;
      setNotice(error instanceof Error ? `Saved documents could not be restored: ${error.message}`
        : 'Saved documents could not be restored.');
    });
    return () => controller.abort();
  }, [api, evidenceContext?.contextId, selectedClient?.id, selectedClient?.name]);

  const updateClientWorkspace = (clientId: string, update: (value: ClientWorkspaceState) => ClientWorkspaceState) =>
    setClientWorkspaces((current) => ({ ...current, [clientId]: update(current[clientId] ?? emptyClientWorkspace(model)) }));

  const clearSelectedClientDrafts = () => {
    setVatBusy(false); setPitBusy(false); setCitBusy(false); setInvoiceBusy(false);
  };
  const selectClient = (clientId: string, obligationId?: TaxObligationId) => {
    const next = model.clients.find((candidate) => candidate.id === clientId);
    if (!next) return;
    const subject = subjects.find((candidate) => candidate.id === clientId);
    const obligations = configuredObligationsForSubject(subject);
    const defaultObligation = obligations.find((obligation) => obligation.id === obligationId)
      ?? obligations.find((obligation) => obligation.capabilities.computation.state === 'AVAILABLE')
      ?? obligations[0];
    const returnContext = defaultObligation ? returnContextForObligation(subject, defaultObligation.id) : undefined;
    api.selectIdentityContexts({
      ...(returnContext?.contextId ? { tax_return: returnContext.contextId }
        : subject?.context_ids?.tax_return ? { tax_return: subject.context_ids.tax_return } : {}),
      ...(subject?.context_ids?.e_invoice ? { e_invoice: subject.context_ids.e_invoice } : {}),
    });
    clearSelectedClientDrafts();
    if (defaultObligation) setSelectedReturnObligation(defaultObligation.id);
    setModel((value) => ({ ...value, selectedClientId: next.id, currentContextId: next.contextId }));
    setActiveView('evidence');
    setNotice(`Opened the authorised workspace for ${next.name}.`);
  };
  const createClientHandoff = (action: 'upload-evidence' | 'inspect-return' | 'declare-sign') => {
    if (!selectedClient) return;
    const client = selectedClient;
    updateClientWorkspace(client.id, (workspace) => ({ ...workspace, handoffs: [{ id: crypto.randomUUID(),
      recipient: `${client.name} · authorised client contact`, action,
      artifactLabel: workspace.taxReturn ? `VAT return · version ${workspace.taxReturn.version}` : presentAppRecordCopy('Evidence workspace', ios),
      state: 'draft', explanation: 'Scoped to this client and awaiting delivery through an authorised channel.' }, ...workspace.handoffs] }));
    setActiveView('handoffs');
    setNotice(`Created a scoped ${action.replaceAll('-', ' ')} handoff for ${client.name}.`);
  };

  async function calculateVat(values: PracticeVatFactsInput): Promise<void> {
    if (!selectedClient) throw new Error('Select an assigned client before calculating VAT.');
    const vatEngagementId = returnContextForObligation(selectedSubject, 'VAT')?.scope.engagementId
      ?? selectedClient.engagementId;
    if (!vatEngagementId) throw new Error('This client assignment is incomplete. Ask a practice administrator to repair it.');
    const client = selectedClient;
    setVatBusy(true);
    try {
      const bound = await bindReviewedVatFacts(values, { taxpayerId: client.id, engagementId: vatEngagementId });
      const calculation = await api.calculateVat(bound.request);
      updateClientWorkspace(client.id, (workspace) => ({ ...workspace,
        vatSnapshotSha256: bound.request.reviewedFactSnapshotSha256, vatCalculation: calculation, taxReturn: undefined,
        calculation: {
          id: calculation.id, version: calculation.packageVersion, rulePackage: calculation.packageId,
          effectiveDate: bound.request.periodStart, status: 'ready-for-professional-review',
          netPositionLabel: `${formatKobo(calculation.liabilityKobo)} estimated payable`, traceAvailable: true,
          lines: calculation.trace.map((line) => ({ id: line.id, label: line.rule,
            amountLabel: formatKobo(line.resultKobo), basis: `Based on reviewed records · reference ${calculation.inputSnapshotSha256.slice(0, 8)}…`,
            sourceRefs: line.sourceIds })),
        }, responsibilities: responsibilitySteps('not-started', 'No authoritative return created') }));
      setNotice(`VAT estimate ready for ${client.name}. Return preparation will remain unavailable until the required professional approval is complete.`);
    } finally { setVatBusy(false); }
  }

  async function prepareVatReturn(): Promise<void> {
    await prepareClientReturn('VAT');
  }

  async function calculatePit(facts: PersonalTaxFactsInput): Promise<void> {
    if (!selectedClient) throw new Error('Select an assigned individual before calculating personal income tax.');
    const context = returnContextForObligation(selectedSubject, 'PIT');
    if (!context) throw new Error('Personal income tax is not configured for this client.');
    const client = selectedClient;
    api.selectContext(context); setPitBusy(true);
    try {
      const periodStart = `${context.scope.periodId.slice(0, 4)}-01-01`;
      const { engineFacts, factSnapshotSha256 } = await bindPersonalTaxFacts(facts, periodStart,
        { taxpayerId: client.id, engagementId: context.scope.engagementId });
      const calculation = await api.calculatePersonalTax({ packageId: facts.packageId, periodStart,
        reviewedFactSnapshotSha256: factSnapshotSha256, facts: engineFacts });
      updateClientWorkspace(client.id, (workspace) => ({ ...workspace, pitCalculation: calculation,
        pitCalculationView: { id: calculation.id, version: calculation.packageVersion, rulePackage: calculation.packageId,
          effectiveDate: periodStart, status: 'ready-for-professional-review',
          netPositionLabel: `${formatKobo(calculation.liabilityKobo)} estimated payable`, traceAvailable: true,
          lines: calculation.trace.map((line) => ({ id: line.id, label: line.rule,
            amountLabel: formatKobo(line.resultKobo), basis: `Based on reviewed personal-tax facts · reference ${calculation.inputSnapshotSha256.slice(0, 8)}…`,
            sourceRefs: line.sourceIds })) }, pitResponsibilities: responsibilitySteps('not-started', 'No authoritative return created') }));
      setNotice(`Personal income tax estimate ready for ${client.name}. Review the calculation trace before return preparation.`);
    } finally { setPitBusy(false); }
  }

  async function calculateCit(values: BusinessCitFactsInput): Promise<void> {
    if (!selectedClient) throw new Error('Select an assigned company before calculating company income tax.');
    const context = returnContextForObligation(selectedSubject, 'CIT');
    if (!context) throw new Error('Company income tax is not configured for this client.');
    const client = selectedClient;
    api.selectContext(context); setCitBusy(true);
    try {
      const request = await bindBusinessCitFacts(values, { taxpayerId: client.id, legalName: client.name,
        engagementId: context.scope.engagementId });
      const calculation = await api.calculateCit(request);
      updateClientWorkspace(client.id, (workspace) => ({ ...workspace, citCalculation: calculation }));
      setNotice(`CIT estimate ready for ${client.name}. Professional review remains required.`);
    } finally { setCitBusy(false); }
  }

  async function prepareClientReturn(obligation: 'VAT' | 'PIT'): Promise<void> {
    const candidate = obligation === 'VAT' ? selectedWorkspace?.taxReturn : selectedWorkspace?.pitReturn;
    if (!selectedClient || !candidate) return;
    const client = selectedClient; const taxReturn = candidate;
    setVatBusy(true);
    try {
      const prepared = await api.mutateReturn(taxReturn.id, 'prepare', 'current-practitioner-preparer');
      updateClientWorkspace(client.id, (workspace) => obligation === 'VAT' ? { ...workspace, taxReturn: prepared,
        responsibilities: responsibilitySteps('prepared', prepared.id) } : { ...workspace, pitReturn: prepared,
        pitResponsibilities: responsibilitySteps('prepared', prepared.id) });
      setNotice(`${obligation} return prepared. Review remains blocked for this preparer and must be completed by another authorised practitioner.`);
    } finally { setVatBusy(false); }
  }

  async function createInvoice(facts: ReviewedInvoiceFactsInput): Promise<void> {
    if (!selectedClient) throw new Error('Select an assigned client before creating an invoice.');
    const client = selectedClient;
    setInvoiceBusy(true);
    try {
      const created = await createPracticeInvoiceDraft(api, client.id, facts);
      updateClientWorkspace(client.id, (workspace) => ({ ...workspace, invoice: created, invoiceReceipt: undefined,
        invoices: [{ id: created.id, buyerName: facts.customer.registrationName,
        amountLabel: formatKobo(created.payableAmountKobo), invoiceNumber: created.invoiceNumber, state: 'draft' },
        ...workspace.invoices.filter((item) => item.id !== created.id)] }));
      setNotice(`Created a reviewed UBL invoice draft for ${client.name}.`);
    } finally { setInvoiceBusy(false); }
  }

  async function actOnInvoice(action: 'prepare' | 'review' | 'approve' | 'mock-dispatch'): Promise<void> {
    if (!selectedClient || !selectedWorkspace?.invoice) return;
    const client = selectedClient; const invoice = selectedWorkspace.invoice;
    if (action !== 'prepare') {
      setNotice(action === 'mock-dispatch'
        ? 'A separately authorised submitter must start the test delivery.'
        : 'This preparer cannot review or approve their own invoice draft. Assign it to another authorised practitioner.');
      return;
    }
    setInvoiceBusy(true);
    try {
      const prepared = await api.mutateInvoice(invoice.id, 'prepare', 'current-practitioner-preparer');
      updateClientWorkspace(client.id, (workspace) => ({ ...workspace, invoice: prepared,
        invoices: workspace.invoices.map((item) => item.id === prepared.id ? { ...item, state: 'needs-review' } : item) }));
      setNotice('Invoice prepared. A different authorised practitioner must review and approve it.');
    } finally { setInvoiceBusy(false); }
  }

  const shared: SharedJourneyPorts = {
    switchContext: ({ contextId }) => {
      const client = model.clients.find((candidate) => candidate.contextId === contextId);
      if (client) selectClient(client.id);
    },
    loadEvidenceActivity: async (limit, signal) => {
      if (!selectedClient || !evidenceContext) return [];
      return (await api.evidenceActivity(selectedClient.id, limit, signal, evidenceContext.contextId)).events;
    },
    registerEvidence: async (file, _context, options) => {
      if (!selectedClient) { setNotice('No assigned client is available for this identity.'); return; }
      const client = selectedClient;
      const ownership = await detectEvidenceOwner(file, client, model.clients);
      let queuedJobId: string | undefined;
      try {
        const response = await api.upload({ client_id: client.id, filename: file.name,
          mime_type: file.type || inferMimeType(file.name), content_base64: await fileToBase64(file),
          ...(options?.modelAssistanceRequested ? { model_assistance_requested: true } : {}) }, options?.signal);
        queuedJobId = response.result.queued_job_id;
        updateClientWorkspace(client.id, (workspace) => ({ ...workspace, evidence: [{ id: response.result.queued_job_id,
          name: file.name, sourceType: evidenceSource(file.name), state: 'checking', addedBy: 'Current practitioner',
          addedAt: 'Just now', hashLabel: 'Integrity check recorded', documentAvailable: true, taxpayerId: client.id,
          taxpayerName: client.name, ownershipState: ownership.state,
          ...(ownership.detectedName ? { ownershipProposal: { detectedName: ownership.detectedName,
            basis: ownership.detectedBy ?? 'browser-source-inspection', extractor: 'browser-source-inspection/2' } } : {}),
          ...(ownership.state === 'mismatch' ? { issue: `Possible wrong client: this file appears to reference ${ownership.detectedName}. Its proposals are blocked until ownership is reviewed.` } : {}) }, ...workspace.evidence] }));
        setNotice(presentAppRecordCopy(ownership.state === 'mismatch' ? 'Evidence secured in ownership review. Processing is running, but its proposals will remain blocked.'
          : 'Evidence secured. Format-appropriate source checks and classification are running.', ios));
        options?.onAccepted?.();
        const job = await api.waitForJob(queuedJobId, { contextId: evidenceContext?.contextId });
        if (job.status === 'failed') {
          if (classificationSourceUnavailable(job)) {
            updateClientWorkspace(client.id, (workspace) => ({ ...workspace,
              evidence: workspace.evidence.map((item) => item.id === queuedJobId ? settleEvidence(item, job) : item) }));
            setNotice('The stored source is unavailable for review. Check its details for recovery options; manual tax amounts remain available.');
            return;
          }
          const promoted = await api.prepareClassificationEvidenceForHumanReview(file, queuedJobId, undefined, evidenceContext);
          updateClientWorkspace(client.id, (workspace) => ({ ...workspace,
            evidence: workspace.evidence.map((item) => item.id === queuedJobId
              ? { ...settleEvidence(item, job), serverEvidenceId: promoted.evidenceId,
                ...(promoted.duplicateOfEvidenceId ? { duplicateOfEvidenceId: promoted.duplicateOfEvidenceId } : {}),
                issue: `${settleEvidence(item, job).issue ?? ''} Review the source and classify it manually if Amoye’s assessment is not correct.`.trim() }
              : item) }));
          setNotice('Amoye could not create reliable proposals. The secured source is available for your review and manual classification.'); return;
        }
        const result = await api.result(queuedJobId, undefined, evidenceContext?.contextId);
        const promoted = await api.promoteClassificationEvidence(file, queuedJobId, undefined, evidenceContext);
        const extractedOwnership = evaluateExtractedOwnership(result.document_analysis, { id: client.id, name: client.name });
        updateClientWorkspace(client.id, (workspace) => {
          const current = workspace.evidence.find((item) => item.id === queuedJobId);
          const ownership = current ? applyExtractedOwnership(settleEvidence(current, job), result,
            { id: client.id, name: client.name }) : undefined;
          const proposals = reconciliationFromResult(result, file.name, queuedJobId).map((proposal) => {
            const serverProposals = (promoted.proposalsByItemId?.[proposal.id] ?? [])
              .filter((candidate) => !candidate.fieldPath.endsWith('.ownership_state'));
            const linked = { ...proposal, serverProposalIds: serverProposals.length
              ? serverProposals.map((candidate) => candidate.id)
              : promoted.proposalIdsByTransactionId[proposal.id] ?? [], serverProposals };
            return applyEvidenceProposalBlockers(linked, ownership?.evidence, promoted.duplicateOfEvidenceId);
          });
          return { ...workspace,
            evidence: workspace.evidence.map((item) => item.id === queuedJobId
              ? { ...applyEvidenceDuplicatePresentation(ownership?.evidence ?? settleEvidence(item, job), promoted.duplicateOfEvidenceId), serverEvidenceId: promoted.evidenceId,
                ...(promoted.processingGrantId ? { processingGrantId: promoted.processingGrantId } : {}),
                ...((promoted.proposalsByItemId?.[queuedJobId!] ?? []).some((proposal) => proposal.fieldPath.endsWith('.ownership_state'))
                  ? { serverOwnershipProposals: (promoted.proposalsByItemId?.[queuedJobId!] ?? [])
                    .filter((proposal) => proposal.fieldPath.endsWith('.ownership_state')) } : {}) } : item),
            reconciliation: [...proposals, ...workspace.reconciliation] };
        });
        setNotice(ownership.state === 'mismatch' || extractedOwnership?.state === 'mismatch'
          ? 'A possible taxpayer mismatch needs review. View the document, then confirm or flag its ownership.'
          : `${result.transactions.length} source-derived proposal${result.transactions.length === 1 ? '' : 's'} ready for human review.`);
      } catch (error) {
        if (queuedJobId) updateClientWorkspace(client.id, (workspace) => ({ ...workspace,
          evidence: workspace.evidence.map((item) => item.id === queuedJobId
            ? settleEvidenceWorkflowError(item, error) : item) }));
        setNotice(error instanceof Error ? error.message : presentAppRecordCopy('Evidence registration failed.', ios));
        if (!queuedJobId) throw error;
      }
    },
    reviewReconciliation: async (item, decision, correction) => {
      if (!selectedClient) return;
      if (decision === 'correct') {
        if (!item.serverProposals?.length || !correction) throw new Error('Enter and save a correction before continuing.');
        await api.correctEvidenceProposals(item.serverProposals, correction);
      }
      if (!item.serverProposalIds?.length) {
        throw new Error('This displayed item is not linked to a durable review record. Upload or reprocess the source before reviewing it.');
      }
      const client = selectedClient;
      if (decision !== 'correct') await api.reviewEvidenceProposals(item.serverProposalIds, decision);
      const nextState: ReconciliationItem['state'] = decision === 'accept'
        ? 'accepted-by-human' : decision === 'correct' ? 'corrected-by-human' : 'excluded-by-human';
      updateClientWorkspace(client.id, (workspace) => {
        const reconciliation = workspace.reconciliation.map((entry) => entry.id === item.id
          ? { ...entry, ...(correction?.ntaCode ? { ntaCode: correction.ntaCode } : {}),
            ...(correction?.vatAmountKobo ? { vatAmountKobo: correction.vatAmountKobo } : {}),
            ...(correction?.evidenceCategory ? { evidenceCategory: correction.evidenceCategory } : {}),
            state: nextState }
          : entry);
        const evidence = applyEvidenceReviewProgress(workspace.evidence, reconciliation, item.evidenceId);
        return { ...workspace, reconciliation, evidence };
      });
      setNotice(decision === 'accept'
        ? `Accepted ${item.description}. VAT fields will refresh from all accepted records.`
        : decision === 'exclude' ? `Excluded ${item.description} from the active tax facts.`
          : `Deferred ${item.description}; it remains blocking.`);
    },
    unlinkEvidence: async (item, input) => {
      if (!selectedClient) return;
      const client = selectedClient;
      if (item.processingGrantId && item.serverEvidenceId) {
        await api.revokeEvidenceProcessing(item.processingGrantId, item.serverEvidenceId);
      }
      await api.deleteClassificationEvidence(item.id, input.reason, input.batchId);
      updateClientWorkspace(client.id, (workspace) => {
        const purged = purgeEvidenceUsage(workspace, item.id);
        return { ...purged, vatCalculation: undefined, vatSnapshotSha256: undefined, pitCalculation: undefined,
          pitCalculationView: undefined, citCalculation: undefined, taxReturn: undefined, pitReturn: undefined,
          calculation: emptyCalculation(model), responsibilities: responsibilitySteps('not-started', presentAppRecordCopy('Invalidated after evidence was deleted', ios)),
          submission: [] };
      });
      setNotice(`${item.name} was permanently deleted for ${client.name}. It will not return after sign-in.`);
    },
    viewEvidence: async (item) => api.evidenceDocument(item.id),
    reviewEvidenceOwnership: async (item, decision) => {
      if (!selectedClient) return;
      const client = selectedClient;
      if (!item.serverOwnershipProposals?.length) throw new Error(item.duplicateOfEvidenceId
        ? 'This copy duplicates an earlier secured source. Resolve ownership on the original source.'
        : 'Ownership review is not ready yet. Resume processing before recording this decision.');
      await api.reviewEvidenceOwnershipProposals(item.serverOwnershipProposals, decision);
      if (decision === 'flag-wrong-taxpayer') {
        if (item.processingGrantId && item.serverEvidenceId) {
          await api.revokeEvidenceProcessing(item.processingGrantId, item.serverEvidenceId);
        }
        await api.deleteClassificationEvidence(item.id, 'wrong-taxpayer');
        updateClientWorkspace(client.id, (workspace) => purgeEvidenceUsage(workspace, item.id));
        setNotice(`${item.name} was flagged as belonging to the wrong taxpayer and permanently deleted for ${client.name}.`); return;
      }
      updateClientWorkspace(client.id, (workspace) => ({ ...workspace,
        evidence: workspace.evidence.map((entry) => entry.id === item.id ? { ...entry, state: 'ready-for-review',
          ownershipState: 'confirmed', issue: 'Ownership confirmed by the practitioner. Review the extracted proposals next.' } : entry),
        reconciliation: workspace.reconciliation.map((entry) => entry.evidenceId === item.id && entry.state === 'blocked'
          ? { ...entry, state: 'needs-review', dispositionReason: undefined } : entry) }));
      setNotice(`Ownership confirmed for ${item.name}. Its proposals are ready for review.`);
    },
    humanClassifyEvidence: async (item, input) => {
      if (!selectedClient) return;
      if (!item.serverEvidenceId) throw new Error('The secured source is not ready for a recorded human decision yet.');
      const client = selectedClient;
      await api.humanClassifyEvidence(item.serverEvidenceId, { ...input, reviewedAt: new Date().toISOString() });
      updateClientWorkspace(client.id, (workspace) => {
        const reconciliation = workspace.reconciliation.map((entry) => entry.evidenceId === item.id && entry.state === 'blocked'
          && entry.periodState !== 'mismatch' && !item.duplicateOfEvidenceId
          ? { ...entry, state: 'needs-review' as const, dispositionReason: undefined } : entry);
        const evidence = workspace.evidence.map((entry) => entry.id === item.id ? { ...entry,
          state: entry.periodState === 'mismatch' ? 'blocked' as const : 'reviewed' as const,
          ownershipState: 'confirmed' as const, classificationCategory: input.evidenceCategory,
          issue: entry.periodState === 'mismatch'
            ? `Category recorded as ${input.evidenceCategory.replaceAll('_', ' ')}, but this source is still outside the selected filing period. Move it to the correct period or remove it from this return.`
            : `Human classification recorded: ${input.evidenceCategory.replaceAll('_', ' ')}. The automated assessment remains in the audit trail.` }
          : entry);
        return { ...workspace, evidence: applyEvidenceReviewProgress(evidence, reconciliation, item.id), reconciliation,
        vatCalculation: undefined, vatSnapshotSha256: undefined, pitCalculation: undefined,
        pitCalculationView: undefined, citCalculation: undefined, taxReturn: undefined, pitReturn: undefined,
        calculation: emptyCalculation(model),
        responsibilities: responsibilitySteps('not-started', presentAppRecordCopy('Invalidated after evidence classification changed', ios)),
        submission: [] };
      });
      setNotice(`${item.name} is now linked as ${input.evidenceCategory.replaceAll('_', ' ')} for ${client.name} based on your recorded review.`);
    },
    performResponsibilityAction: (action) => {
      if (action === 'prepare' && (selectedReturnObligation === 'VAT' || selectedReturnObligation === 'PIT'))
        void prepareClientReturn(selectedReturnObligation);
      else setNotice(responsibilityHandoffNotice(action));
    },
    reconcileBridge: () => {
      if (!selectedClient) return;
      updateClientWorkspace(selectedClient.id, (workspace) => ({ ...workspace, bridge: { ...workspace.bridge,
        reconciliationRequired: false, retryAllowed: true, detail: 'The prior test request was checked. No tax-authority outcome was created.' } }));
    },
    retryBridge: () => setNotice('The unchanged invoice data was queued again for test delivery.'),
    setIntelligenceEnabled: (enabled) => {
      if (!selectedClient) return;
      updateClientWorkspace(selectedClient.id, (workspace) => ({ ...workspace,
        intelligence: { ...workspace.intelligence, enabled, status: enabled ? 'ready' : 'off' } }));
    },
    loadHomeSuggestions: signal => api.loadHomeSuggestions({ rail: 'tax_return' }, signal),
    queryIntelligence: async (prompt, consentEvidenceId, signal, historyMode = 'EPHEMERAL') => {
      const contextKind = activeView === 'portfolio' || activeView === 'home' || activeView === 'intelligence'
        ? 'PORTFOLIO' as const : 'CLIENT' as const;
      const response = await api.queryAssistantIntelligence({ rail: 'tax_return', prompt,
        consentEvidenceId, historyMode, contextKind }, signal);
      return { answer: response.answer, citations: response.citations.map(({
        sourceId, sourceVersion, locator, title, authority, sourceUri,
      }) => ({ sourceId, sourceVersion, locator, title, authority, sourceUri })),
        limitations: response.limitations, generatedAt: response.generatedAt,
        modelId: response.modelId, modelVersion: response.modelVersion,
        activities: response.activities, navigationIntents: response.navigationIntents,
        historyMode: response.conversation.historyMode, retainedUntil: response.conversation.retainedUntil };
    },
    loadIntelligenceHistory: async (consentEvidenceId, signal) => {
      const contextKind = activeView === 'portfolio' || activeView === 'home' || activeView === 'intelligence'
        ? 'PORTFOLIO' as const : 'CLIENT' as const;
      return (await api.loadAssistantHistory({ rail: 'tax_return', consentEvidenceId, contextKind }, signal)).turns
        .map((turn) => ({ ...turn, response: { answer: turn.response.answer,
          citations: turn.response.citations.map(({ sourceId, sourceVersion, locator, title, authority, sourceUri }) =>
            ({ sourceId, sourceVersion, locator, title, authority, sourceUri })),
          limitations: turn.response.limitations, generatedAt: turn.response.generatedAt,
          modelId: turn.response.modelId, modelVersion: turn.response.modelVersion,
          activities: turn.response.activities, navigationIntents: turn.response.navigationIntents,
          historyMode: turn.response.conversation.historyMode,
          retainedUntil: turn.response.conversation.retainedUntil } }));
    },
    deleteIntelligenceHistory: (consentEvidenceId, signal) => api.deleteAssistantHistory({
      rail: 'tax_return', consentEvidenceId,
      contextKind: activeView === 'portfolio' || activeView === 'home' || activeView === 'intelligence'
        ? 'PORTFOLIO' : 'CLIENT' }, signal),
    openIntelligenceIntent: (intent) => {
      if (intent.target.resourceRef) {
        const client = model.clients.find((candidate) => candidate.id === intent.target.resourceRef);
        if (!client) {
          setNotice('That client link is stale or no longer authorised. Refresh the portfolio guidance before opening it.');
          return false;
        }
        selectClient(client.id);
        return true;
      }
      if (intent.kind === 'OPEN_CLIENT') return false;
      return undefined;
    },
  };
  const scopedModel: TrackAPracticeModel = { ...model,
    workItems: currentWorkItems,
    evidence: selectedWorkspace?.evidence ?? [], reconciliation: selectedWorkspace?.reconciliation ?? [],
    calculation: selectedReturnObligation === 'PIT' ? selectedWorkspace?.pitCalculationView ?? emptyCalculation(model)
      : selectedWorkspace?.calculation ?? model.calculation,
    responsibilities: selectedReturnObligation === 'PIT' ? selectedWorkspace?.pitResponsibilities ?? responsibilitySteps('not-started', 'Not created')
      : selectedWorkspace?.responsibilities ?? model.responsibilities,
    submission: selectedWorkspace?.submission ?? model.submission, bridge: selectedWorkspace?.bridge ?? model.bridge,
    invoices: selectedWorkspace?.invoices ?? [], handoffs: selectedWorkspace?.handoffs ?? [],
    intelligence: selectedWorkspace?.intelligence ?? model.intelligence };
  const invoiceView: InvoiceResourceView | undefined = selectedWorkspace?.invoice ? {
    id: selectedWorkspace.invoice.id, state: selectedWorkspace.invoice.state,
    invoiceNumber: selectedWorkspace.invoice.invoiceNumber,
    payableAmountKobo: selectedWorkspace.invoice.payableAmountKobo,
    payloadSha256: selectedWorkspace.invoice.regulatedPayload.payloadSha256,
    validationState: selectedWorkspace.invoice.regulatedPayload.validation.state,
  } : undefined;
  const vatInteraction = selectedClient && <PracticeVatWorkflow key={`vat-${selectedClient.id}`} clientName={selectedClient.name}
    taxReturn={selectedWorkspace?.taxReturn} calculation={selectedWorkspace?.vatCalculation}
    snapshotSha256={selectedWorkspace?.vatSnapshotSha256} derivation={vatDerivation} busy={vatBusy}
    onCalculate={calculateVat} onPrepare={prepareVatReturn} onCreateHandoff={createClientHandoff} />;
  const pitContext = returnContextForObligation(selectedSubject, 'PIT');
  const pitInteraction = selectedClient && pitContext && <PersonalTaxFactsForm key={`pit-${selectedClient.id}-${pitContext.scope.packageId}`}
    initial={{ ...trackB2Fixture.taxFacts, packageId: pitContext.scope.packageId as PersonalTaxFactsInput['packageId'] }}
    allowedPackages={[pitContext.scope.packageId as PersonalTaxFactsInput['packageId']]}
    enabled={!pitBusy} onCalculate={calculatePit} />;
  const citInteraction = selectedClient && <BusinessCitReturnInteraction key={`cit-${selectedClient.id}`}
    businessName={selectedClient.name} busy={citBusy} calculation={selectedWorkspace?.citCalculation}
    onCalculate={calculateCit} />;
  const returnObligations = configuredObligationsForSubject(selectedSubject).map((obligation) =>
    toReturnCentreObligation(obligation, obligation.id === 'VAT'
      ? { ...(selectedWorkspace?.taxReturn ? { taxReturn: selectedWorkspace.taxReturn } : {}), interaction: vatInteraction }
      : obligation.id === 'PIT' ? { ...(selectedWorkspace?.pitReturn ? { taxReturn: selectedWorkspace.pitReturn } : {}),
        interaction: pitInteraction }
        : obligation.id === 'CIT' ? { interaction: citInteraction } : {}));
  const selectReturnObligation = (obligation: TaxObligationId) => {
    const context = returnContextForObligation(selectedSubject, obligation);
    if (context) api.selectContext(context);
    setSelectedReturnObligation(obligation);
  };
  const returnCentre = selectedClient && <ReturnCentre subjectName={selectedClient.name} selectedObligation={selectedReturnObligation}
    onSelectObligation={selectReturnObligation} obligations={returnObligations} />;
  return <>{notice && <p aria-live="polite" className="am-session-only">{notice}</p>}<TrackAPracticeJourney activeView={activeView} model={scopedModel} ports={{
    ...shared, addClient: () => setAddingClient(true), selectClient, navigate: setActiveView,
    openWorkItem: (id) => {
      const work = currentWorkItems.find((item) => item.id === id);
      if (!work) return;
      selectClient(work.clientId);
      setActiveView(work.destination ?? (work.title.toLowerCase().includes('declaration') ? 'handoffs' : 'evidence'));
      setNotice(`Opened ${work.title} for ${work.clientName}.`);
    }, createClientHandoff,
    openInvoice: (id) => { setActiveView('invoices'); setNotice(`Opened standards invoice draft ${id}.`); },
  }} vatInteraction={returnCentre} returnDetailsVisible={returnObligations.some((item) => item.id === selectedReturnObligation)}
    invoiceInteraction={selectedClient && <PracticeInvoiceWorkflow key={`invoice-${selectedClient.id}`}
      businessName={selectedClient.name} invoice={invoiceView} receipt={selectedWorkspace?.invoiceReceipt} busy={invoiceBusy}
      onCreate={createInvoice} onAction={actOnInvoice} />} />
    {addingClient && <TaxSubjectSetupDialog api={api} product="practitioner" onCancel={() => setAddingClient(false)}
      onComplete={(subject) => { setAddingClient(false); onTaxSubjectCreated?.(subject); }} />}</>;
}

function buildInitialModel(subjects: readonly AccountSubject[]): TrackAPracticeModel {
  const initial = structuredClone(trackAFixture);
  const assigned = subjects.filter((subject): subject is AssignedClientSubject => subject.kind === 'client'
    || (subject.kind === 'individual' && Boolean(subject.engagement_id)));
  const templateContext = initial.contexts[0];
  initial.clients = assigned.map((subject, index) => {
    const template = initial.clients[index % initial.clients.length]!;
    const contextId = `ctx-assigned-${index}`;
    const engagementId = subject.engagement_id ?? (import.meta.env.DEV ? `local-a-engagement-${index}` : '');
    const deadlines = filingDeadlineNoticesForSubject(subject);
    const actingCapacity = subject.resolved_contexts?.[0]?.actingCapacity;
    return { ...template, id: subject.id, contextId, engagementId, name: subject.display_name,
      taxIdLabel: subject.tax_id_hint
        ? `Tax ID ending ${subject.tax_id_hint} · ${subject.tax_identity_status === 'VERIFIED' ? 'verified' : 'verification pending'}`
        : 'Tax ID not provided',
      assignment: actingCapacity === 'practice_admin' ? 'Current account owner · Practice administrator'
        : actingCapacity ? `Current user · ${actingCapacity.replaceAll('_',' ')}` : 'Assignment not confirmed',
      nextDeadline: deadlines[0]?.dateLabel ?? 'Not confirmed', deadlines,
      openExceptions: 0,
      returnState: 'evidence-needed' as const, invoiceState: 'not-started' as const };
  });
  initial.contexts = templateContext ? initial.clients.map((client) => ({ ...templateContext, id: client.contextId,
    workspaceName: assigned.find((subject) => subject.id === client.id)?.workspace_name ?? 'Tax practice',
    subjectName: client.name, assignmentLabel: client.assignment })) : [];
  initial.workItems = [];
  initial.selectedClientId = initial.clients[0]?.id ?? '';
  initial.currentContextId = initial.clients[0]?.contextId ?? '';
  initial.responsibilities = responsibilitySteps('not-started', 'Not created');
  initial.calculation = { ...initial.calculation, status: 'draft', netPositionLabel: 'Not calculated', lines: [] };
  return initial;
}

function buildClientWorkspaces(model: TrackAPracticeModel): Record<string, ClientWorkspaceState> {
  // Authenticated workspaces start from durable server activity. Component
  // fixtures must never masquerade as reviewable evidence in the live UI.
  return Object.fromEntries(model.clients.map((client) => [client.id, emptyClientWorkspace(model)]));
}

function emptyClientWorkspace(model: TrackAPracticeModel): ClientWorkspaceState {
  return { evidence: [], reconciliation: [],
    calculation: { ...structuredClone(model.calculation), status: 'draft', netPositionLabel: 'Not calculated', lines: [] },
    responsibilities: responsibilitySteps('not-started', 'Not created'), submission: [],
    bridge: { ...structuredClone(model.bridge), reconciliationRequired: false, retryAllowed: false,
      detail: 'No standards-bridge request has been made for this client.' },
    invoices: [], handoffs: [], intelligence: { ...structuredClone(model.intelligence), insights: [] } };
}

function emptyCalculation(model: TrackAPracticeModel): CalculationSnapshot {
  return { ...structuredClone(model.calculation), status: 'draft', netPositionLabel: 'Not calculated', lines: [] };
}

function responsibilitySteps(stage: 'not-started' | 'calculated' | 'prepared', artifact: string): TrackAPracticeModel['responsibilities'] {
  const prepared = stage === 'prepared';
  return [
    { id: 'rs-1', action: 'prepare', actorLabel: 'Current authorised practitioner · Preparer',
      state: stage === 'calculated' ? 'ready' : prepared ? 'complete' : 'waiting', artifactVersion: artifact,
      explanation: prepared ? 'Prepared from the reviewed figures and recorded calculation steps.' : 'Available after the reviewed figures and calculation are recorded.' },
    { id: 'rs-2', action: 'professional-review', actorLabel: 'Different authorised practitioner · Reviewer', state: 'blocked',
      artifactVersion: artifact, explanation: 'The preparer cannot review their own return.' },
    { id: 'rs-3', action: 'approve', actorLabel: 'Different authorised practitioner · Approver', state: 'blocked',
      artifactVersion: artifact, explanation: 'Approval follows independent professional review.' },
    { id: 'rs-4', action: 'declare-sign', actorLabel: 'Authorised client actor · Signatory', state: 'blocked',
      artifactVersion: artifact, explanation: 'The client signs the exact approved immutable version.' },
    { id: 'rs-5', action: 'request-mock-submission', actorLabel: 'Separately authorised actor · Submitter', state: 'blocked',
      artifactVersion: artifact, explanation: 'Submission authority remains separate. This environment performs delivery tests only.' },
  ];
}

function responsibilityHandoffNotice(action: ResponsibilityAction): string {
  if (action === 'professional-review' || action === 'approve') return 'A different authorised practitioner must complete this step.';
  if (action === 'declare-sign') return 'Create a client handoff so an authorised client actor can inspect and sign the bound artifact.';
  if (action === 'request-mock-submission') return 'A separately authorised submitter must request the delivery test.';
  return 'Complete the tax calculation before preparing the return.';
}

function evidenceSource(name: string): 'controlled-csv' | 'receipt' | 'bank-statement' | 'pos-export' {
  const extension = name.toLowerCase().split('.').at(-1);
  if (extension === 'pdf') return 'bank-statement';
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension ?? '')) return 'receipt';
  if (['xlsx', 'json'].includes(extension ?? '')) return 'pos-export';
  return 'controlled-csv';
}

function inferMimeType(name: string): string {
  const extension = name.toLowerCase().split('.').at(-1);
  return extension === 'csv' ? 'text/csv' : extension === 'json' ? 'application/json'
    : extension === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : extension === 'pdf' ? 'application/pdf' : ['jpg', 'jpeg'].includes(extension ?? '') ? 'image/jpeg'
        : extension === 'png' ? 'image/png' : extension === 'webp' ? 'image/webp' : 'application/octet-stream';
}
